import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

export interface BackendErrorResponse {
  status: number;
  hour: string;
  customMessage: string;
  fields: string[];
  serproMessage: boolean;
}

export enum BackendErrorCode {
  CPF_ALREADY_REGISTERED = 'CPF_ALREADY_REGISTERED',
  MAIL_ALREADY_REGISTERED = 'MAIL_ALREADY_REGISTERED',
  MAIL_NOT_ALREADY_REGISTERED = 'MAIL_NOT_ALREADY_REGISTERED',
  CNPJ_ALREADY_REGISTERED = 'CNPJ_ALREADY_REGISTERED',
  PARTNER_ALREADY_REGISTERED = 'PARTNER_ALREADY_REGISTERED',
  MAIL_DOES_NOT_EXISTS_OR_IS_INACTIVE = 'MAIL_DOES_NOT_EXISTS_OR_IS_INACTIVE',
  INVALID_PASSWORD = 'INVALID_PASSWORD',
  INVALID_CNPJ = 'INVALID_CNPJ',
  INVALID_HASH = 'INVALID_HASH',
  INVALID_TOKEN = 'INVALID_TOKEN',
  USER_OR_TENANT_INFORMATION_NOT_FOUND = 'USER_OR_TENANT_INFORMATION_NOT_FOUND',
  USER_NOT_FOUND = 'USER_NOT_FOUND',
  COMPANY_NOT_FOUND = 'COMPANY_NOT_FOUND',
  PARTNER_DOCUMENT_NOT_FOUND = 'PARTNER_DOCUMENT_NOT_FOUND',
  COMPANY_DOCUMENT_NOT_FOUND = 'COMPANY_DOCUMENT_NOT_FOUND',
  QUOTATION_DOCUMENTS_NOT_FOUND = 'QUOTATION_DOCUMENTS_NOT_FOUND',
  TENANT_NOT_FOUND = 'TENANT_NOT_FOUND',
  EMPLOYEE_NOT_FOUND = 'EMPLOYEE_NOT_FOUND',
  DATA_NOT_FOUND = 'DATA_NOT_FOUND',
  DOCUMENT_REJECTED_OR_OUTDATED_CANNOT_BE_CHANGED = 'DOCUMENT_REJECTED_OR_OUTDATED_CANNOT_BE_CHANGED',
  THERE_IS_NO_FILE_YET = 'THERE_IS_NO_FILE_YET',
  NOT_UPLOADED_FILE = 'NOT_UPLOADED_FILE',
  PHONE_ALREADY_REGISTERED = 'PHONE_ALREADY_REGISTERED',
  PARENT_TENANT_NOT_FOUND = 'PARENT_TENANT_NOT_FOUND',
  INCOMPLETE_REGISTRATION = 'INCOMPLETE_REGISTRATION',
  ACTION_NOT_ALLOWED = 'ACTION_NOT_ALLOWED',
  ACTION_NOT_ALLOWED_COMPANY_NOT_PROVIDER = 'ACTION_NOT_ALLOWED_COMPANY_NOT_PROVIDER',
  ACTION_NOT_ALLOWED_PROVIDER_FIDUCIA_NOT_ALLOWED_DISABLE_FIDUCIA_SIMULATION = 'ACTION_NOT_ALLOWED_PROVIDER_FIDUCIA_NOT_ALLOWED_DISABLE_FIDUCIA_SIMULATION',
  TENANT_WITHOUT_CCB_PROVIDER = 'TENANT_WITHOUT_CCB_PROVIDER',
  EMAIL_ALREADY_EXISTS_FOR_THIS_COMPANY = 'EMAIL_ALREADY_EXISTS_FOR_THIS_COMPANY',
  PHONE_ALREADY_EXISTS_FOR_THIS_COMPANY = 'PHONE_ALREADY_EXISTS_FOR_THIS_COMPANY',
}

@Injectable({
  providedIn: 'root',
})
export class ErrorMessageService {
  private readonly messages: Record<BackendErrorCode, string> = {
    [BackendErrorCode.CPF_ALREADY_REGISTERED]:
      'Ocorreu um erro ao tentar criar sua conta. Este CPF já está cadastrado no sistema.',
    [BackendErrorCode.MAIL_ALREADY_REGISTERED]:
      'Ocorreu um erro ao tentar criar sua conta. Este e-mail já está cadastrado no sistema.',
    [BackendErrorCode.CNPJ_ALREADY_REGISTERED]:
      'Ocorreu um erro ao tentar criar sua conta. Este CNPJ já está cadastrado no sistema.',
    [BackendErrorCode.PARTNER_ALREADY_REGISTERED]:
      'Ocorreu um erro ao tentar criar sua conta. Este sócio já está cadastrado no sistema.',
    [BackendErrorCode.MAIL_DOES_NOT_EXISTS_OR_IS_INACTIVE]:
      'Ocorreu um erro ao tentar criar sua conta. E-mail não encontrado ou inativo.',
    [BackendErrorCode.INVALID_PASSWORD]: 'Senha inválida.',
    [BackendErrorCode.INVALID_CNPJ]:
      'Ocorreu um erro ao tentar criar sua conta. CNPJ inválido.',
    [BackendErrorCode.INVALID_HASH]: 'Hash de verificação inválido.',
    [BackendErrorCode.INVALID_TOKEN]: 'Token de autenticação inválido.',
    [BackendErrorCode.USER_OR_TENANT_INFORMATION_NOT_FOUND]:
      'Informações de usuário ou empresa não encontradas.',
    [BackendErrorCode.USER_NOT_FOUND]: 'Usuário não encontrado.',
    [BackendErrorCode.COMPANY_NOT_FOUND]: 'Empresa não encontrada.',
    [BackendErrorCode.PARTNER_DOCUMENT_NOT_FOUND]:
      'Documento do sócio não encontrado.',
    [BackendErrorCode.COMPANY_DOCUMENT_NOT_FOUND]:
      'Documento da empresa não encontrado.',
    [BackendErrorCode.QUOTATION_DOCUMENTS_NOT_FOUND]:
      'Documentos da cotação não encontrados.',
    [BackendErrorCode.TENANT_NOT_FOUND]: 'Empresa não encontrada.',
    [BackendErrorCode.EMPLOYEE_NOT_FOUND]: 'Funcionário não encontrado.',
    [BackendErrorCode.DATA_NOT_FOUND]: 'Dados não encontrados.',
    [BackendErrorCode.DOCUMENT_REJECTED_OR_OUTDATED_CANNOT_BE_CHANGED]:
      'Documento rejeitado ou desatualizado não pode ser alterado.',
    [BackendErrorCode.THERE_IS_NO_FILE_YET]: 'Ainda não há arquivo disponível.',
    [BackendErrorCode.NOT_UPLOADED_FILE]: 'Arquivo não foi enviado.',
    [BackendErrorCode.PHONE_ALREADY_REGISTERED]:
      'Ocorreu um erro ao tentar criar sua conta. Este telefone já está cadastrado no sistema.',
    [BackendErrorCode.PARENT_TENANT_NOT_FOUND]: 'Empresa não encontrada.',
    [BackendErrorCode.INCOMPLETE_REGISTRATION]:
      'Cadastro incompleto. Por favor, complete todas as informações.',
    [BackendErrorCode.ACTION_NOT_ALLOWED]: 'Ação não permitida.',
    [BackendErrorCode.ACTION_NOT_ALLOWED_COMPANY_NOT_PROVIDER]:
      'Ação não permitida. A empresa não é um fornecedor.',
    [BackendErrorCode.ACTION_NOT_ALLOWED_PROVIDER_FIDUCIA_NOT_ALLOWED_DISABLE_FIDUCIA_SIMULATION]:
      'Ação não permitida. Um Fornecedor Fidúcia não pode desabilitar a simulação Fidúcia.',
    [BackendErrorCode.TENANT_WITHOUT_CCB_PROVIDER]:
      'Empresa sem fornecedor CCB configurado.',
    [BackendErrorCode.MAIL_NOT_ALREADY_REGISTERED]:
      'O e-mail informado não está cadastrado.',
    [BackendErrorCode.EMAIL_ALREADY_EXISTS_FOR_THIS_COMPANY]:
      'O e-mail informado já está cadastrado para esta empresa.',
    [BackendErrorCode.PHONE_ALREADY_EXISTS_FOR_THIS_COMPANY]:
      'O telefone informado já está cadastrado para esta empresa.',
  };

  getMessage(
    errorCode: BackendErrorCode,
    { customMessage }: { customMessage: string } = {
      customMessage: 'Ops! Algo não saiu como esperado.',
    },
  ): string {
    return this.messages[errorCode] || customMessage;
  }

  hasMessage(errorCode: string): errorCode is BackendErrorCode {
    return Object.values(BackendErrorCode).includes(
      errorCode as BackendErrorCode,
    );
  }
}

@Injectable({
  providedIn: 'root',
})
export class ErrorInterceptorService implements HttpInterceptor {
  private readonly SKIP_ERROR_HANDLING_HEADER = 'skip-error-handling';

  constructor(
    private snackbarService: MatSnackBar,
    private errorMessageService: ErrorMessageService,
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (this.shouldSkipErrorHandling(req)) {
      return this.handleWithoutErrorInterceptor(req, next);
    }

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        this.handleError(error);
        return throwError(error);
      }),
    );
  }

  private shouldSkipErrorHandling(req: HttpRequest<any>): boolean {
    return req.headers.has(this.SKIP_ERROR_HANDLING_HEADER);
  }

  private handleWithoutErrorInterceptor(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const modifiedReq = req.clone({
      headers: req.headers.delete(this.SKIP_ERROR_HANDLING_HEADER),
    });
    return next.handle(modifiedReq);
  }

  private handleError(error: HttpErrorResponse): void {
    console.error(error);
    if (error.status === 401 || error.status === 403) {
      return;
    }

    const errorResponse = error.error as BackendErrorResponse;

    if (errorResponse?.serproMessage && errorResponse?.customMessage) {
      const message: string = `Ocorreu um erro ao processar a requisição: ${errorResponse.customMessage} Por favor, tente novamente.`;

      this.showErrorNotification(message);
      return;
    }

    const errorCode = this.extractErrorCode(error);

    if (errorCode && this.errorMessageService.hasMessage(errorCode)) {
      const message = this.errorMessageService.getMessage(errorCode);
      this.showErrorNotification(message);
      return;
    }

    this.showGenericError();
  }

  private extractErrorCode(error: HttpErrorResponse): string | null {
    const errorResponse = error.error as BackendErrorResponse;

    if (errorResponse && errorResponse.customMessage) {
      return errorResponse.customMessage;
    }

    return null;
  }

  private showErrorNotification(message: string): void {
    this.snackbarService.open(message, '', {
      duration: 4000, //  4 seconds
    });
  }

  private showGenericError(): void {
    let message = 'Ops! Algo não saiu como esperado. Tente novamente.';
    this.showErrorNotification(message);
  }
}
